import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AssistantGroundingSchema, { AssistantGroundingJSON, AssistantGroundingParams } from "ai-assistant/grounding/assistant-grounding-schema";
import { AssistantGroundingState, needsAGroundingNotice } from "ai-assistant/grounding/assistant-grounding-state";

/**
 * How much of one answer the server is willing to say it checked.
 *
 * The counts are here and the claims are not. A count is a fact about the turn; the sentence
 * behind it is in the answer text, marked `[[?]]` where the attribution was withdrawn, which
 * is where a reader finds it.
 */
export default class AssistantGrounding {
  /**
   * What a turn that said nothing about its grounding means.
   *
   * An API from before the field existed, a locally-built message, or the user's own turn.
   * NotApplicable rather than Grounded: neither of those turns was checked, and reading an
   * absent field as a pass is the same mistake this whole feature is about.
   */
  public static readonly unstated = new AssistantGrounding({
    state: AssistantGroundingState.NotApplicable,
    unverifiedClaims: 0,
    uncitedClaims: 0,
    unruledClaims: 0,
  });

  public static create(values: AssistantGroundingParams): AssistantGrounding {
    const parsed = AssistantGroundingSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantGrounding parameters.");
    }
    return new AssistantGrounding(parsed);
  }

  /**
   * Deliberately unlike its siblings: an absent block becomes {@link unstated} rather than
   * being handed back as null.
   *
   * The other `fromJSON` methods in this domain return the nullish input under a non-nullable
   * return type, which every caller then has to know about. Here there is a right answer for
   * an absent block - the turn was not assessed - so returning it is both safer and more
   * truthful than propagating a null into code that assumes an instance.
   */
  public static fromJSON(json: AssistantGroundingJSON): AssistantGrounding {
    if (isNullOrUndefined(json)) return AssistantGrounding.unstated;
    return new AssistantGrounding(AssistantGroundingSchema.deserialize(json));
  }

  public readonly state: AssistantGroundingState;
  public readonly unverifiedClaims: number;
  public readonly uncitedClaims: number;
  public readonly unruledClaims: number;

  /** Whether the reader should be told this answer was not fully checked. */
  public get needsANotice(): boolean {
    return needsAGroundingNotice(this.state);
  }

  public constructor(params: AssistantGroundingParams) {
    this.state = params.state;
    this.unverifiedClaims = params.unverifiedClaims;
    this.uncitedClaims = params.uncitedClaims;
    this.unruledClaims = params.unruledClaims;
  }

  public toJSON(): AssistantGroundingJSON {
    return AssistantGroundingSchema.serialize(this);
  }

  public isEqualTo(other: AssistantGrounding): boolean {
    return this.state === other.state
      && this.unverifiedClaims === other.unverifiedClaims
      && this.uncitedClaims === other.uncitedClaims
      && this.unruledClaims === other.unruledClaims;
  }
}
